// Logic thuần cho màn duyệt khung xương (stage ⓪, đợt 260812).
//
// 🔴 File này CỐ Ý không import antd: antd v6 + react-router-dom v7 không load được dưới jest của
// react-scripts, nên mọi logic cần test phải nằm ngoài file có antd (khuôn sẵn có: filmDraftStruct.ts,
// worldSettingUtils.ts, apiErrorText.ts).
import type {
  AdaptationProgress, AliasCandidate, EpisodeScriptRecord, LocalizeMode, SourceRange, CrawlProgressData, EpisodeScenePlanItem, FilmGlossaryType,
  FilmOutline, FilmWriteScriptsTaskDetail, IssueKind, LocalizeView, OutlineBeat, OutlineViolation, ScanProgressData,
} from '../../types/adaptation';
import { issueWhere } from './gateView';
import { redactMoney } from './workbench/moneyRedact';
import { isOverviewWriterCode, overviewErrorView } from './workbench/overview/overviewErrors';

export interface OutlineSummary {
  leads: number;
  support: number;
  functional: number;
  charactersTotal: number;
  primaryLocations: number;
  otherLocations: number;
  beats: number;
  majorBeats: number;
  episodesWritten: number;
  episodesPlanned: number;
  /**
   * Chưa có nhân vật chính nào — lỗi thật (không dựng được phim), khác với "đếm". Số nhân vật / bối cảnh KHÔNG có giới hạn
   * (User chốt 07/10) nên không còn trần nào để tô đỏ.
   */
  noLead: boolean;
}

export const summarizeOutline = (outline: FilmOutline | null): OutlineSummary => {
  const empty: OutlineSummary = {
    leads: 0, support: 0, functional: 0, charactersTotal: 0,
    primaryLocations: 0, otherLocations: 0,
    beats: 0, majorBeats: 0, episodesWritten: 0, episodesPlanned: 0, noLead: false,
  };
  if (!outline) return empty;

  const chars = outline.characters ?? [];
  const locs = outline.locations ?? [];
  const beats = outline.beats ?? [];
  const planned = outline.params?.episodes ?? 0;

  const s: OutlineSummary = {
    leads: chars.filter((c) => c.tier === 'lead').length,
    support: chars.filter((c) => c.tier === 'support').length,
    functional: chars.filter((c) => c.tier === 'functional').length,
    charactersTotal: chars.length,
    primaryLocations: locs.filter((l) => l.primary).length,
    otherLocations: locs.filter((l) => !l.primary).length,
    beats: beats.length,
    majorBeats: beats.filter((b) => b.weight === 'major').length,
    episodesWritten: (outline.episodes ?? []).length,
    episodesPlanned: planned,
    noLead: false,
  };
  s.noLead = s.leads < 1;

  return s;
};

export interface GroupedViolations {
  errors: OutlineViolation[];
  warnings: OutlineViolation[];
  byGate: Array<{ gate: string; count: number; severity: OutlineViolation['severity'] }>;
}

/**
 * Gộp vi phạm trùng `(gate, message)` thành 1 dòng, hợp nhất `refs` — không mất ref nào, không lặp.
 * Đợt 260826, Epic 1 Phase 2: modal kịch bản từng hiện 3 lần y hệt câu "Tập chưa có chương nguồn để
 * đối chiếu" vì server trả 1 violation cho mỗi cảnh thiếu evidence, cùng gate + cùng message.
 */
export const dedupeViolations = (violations: OutlineViolation[]): OutlineViolation[] => {
  const order: string[] = [];
  const byKey = new Map<string, OutlineViolation>();
  for (const v of violations) {
    // 261015 P2b-2 (B2): `superseded` nằm TRONG khoá — bản lưu đã bị thay (kind `retired`, FE ẩn) và bản TÍNH LẠI cùng gate+message không được gộp, nếu không bản mới nhập vào bản cũ rồi biến mất cùng chip.
    const key = `${v.gate}|${v.message}|${v.superseded === true ? 'S' : ''}`;
    const existing = byKey.get(key);
    if (!existing) {
      order.push(key);
      byKey.set(key, { ...v, refs: Array.from(new Set(v.refs ?? [])) });
      continue;
    }
    const mergedRefs = new Set([...(existing.refs ?? []), ...(v.refs ?? [])]);
    byKey.set(key, { ...existing, refs: Array.from(mergedRefs) });
  }
  return order.map((key) => byKey.get(key) as OutlineViolation);
};

// ── 261015 P1: tách LỖI THẬT khỏi điểm · thông báo · chưa kiểm · cần chạy lại ─────────────────────

export type { IssueKind };
export type OtherIssueKind = 'score' | 'notice' | 'unchecked' | 'stale' | 'accepted';   // 261015 P2b-1: + 'accepted' (người đã chấp nhận có lý do — khối gập, không chip)

export interface SplitIssues<T> {
  defects: T[];
  others: Record<OtherIssueKind, T[]>;
}

/**
 * Tách theo `kind`. 🔴 THIẾU `kind` ⇒ `defect` (BE cũ / response lạ không được làm mất lỗi); `retired` BỎ HẲN khỏi cả hai nhóm.
 * Generic để caller ghép trường phụ (vd id chip theo chỉ số GỐC) TRƯỚC khi tách — tách trước rồi tra id theo index sẽ lệch.
 */
/**
 * 261015 P3a — 4 mã «ý của tập» thuộc chặng ④ (chip + trợ lý ④ `rewriteText`); ② không có lệnh nào xử lý ⇒ dải lỗi ② bỏ chúng (BE cũng bỏ khỏi chip ②).
 * 261015 P4c-2b (D4) — thêm 2 mã nhịp hấp dẫn (WARN, notice/unchecked): `EPISODE_FLAT_RUN` · `EPISODE_APPEAL_UNCHECKED`. BẢN SAO của BE `EPISODE_CLAIM_GATES` (`episodeClaims.ts`) — đổi một bên thì đổi cả hai.
 */
export const EPISODE_CLAIM_GATES: ReadonlySet<string> = new Set([
  'EPISODE_CLAIM_UNVERIFIED', 'EPISODE_CLAIMS_STALE', 'EPISODE_CLAIMS_LEGACY', 'EPISODE_HOOK_CLAIMS_INVALID',
  'EPISODE_FLAT_RUN', 'EPISODE_APPEAL_UNCHECKED',
]);
export const withoutEpisodeClaimCodes = <T extends { gate: string }>(list: ReadonlyArray<T> | null | undefined): T[] => (Array.isArray(list) ? list : []).filter((v) => !EPISODE_CLAIM_GATES.has(v.gate));

export const splitIssuesByKind = <T extends { kind?: IssueKind }>(list: ReadonlyArray<T> | null | undefined): SplitIssues<T> => {
  const out: SplitIssues<T> = { defects: [], others: { score: [], notice: [], unchecked: [], stale: [], accepted: [] } };
  for (const v of Array.isArray(list) ? list : []) {
    const kind = v?.kind ?? 'defect';
    if (kind === 'retired') continue;
    if (kind === 'defect') out.defects.push(v);
    else if (kind in out.others) out.others[kind as OtherIssueKind].push(v);
    else out.defects.push(v); // kind lạ ⇒ thận trọng, coi là lỗi
  }
  return out;
};

export const kindLabel = (kind: OtherIssueKind): string => ({
  score: 'Điểm', notice: 'Thông báo', unchecked: 'Chưa kiểm', stale: 'Cần chạy lại', accepted: 'Đã chấp nhận',
} as Record<OtherIssueKind, string>)[kind];

export const groupViolations = (violations: OutlineViolation[] | null | undefined): GroupedViolations => {
  const list = violations ?? [];
  const counts = new Map<string, { count: number; severity: OutlineViolation['severity'] }>();
  for (const v of list) {
    const cur = counts.get(v.gate);
    counts.set(v.gate, { count: (cur?.count ?? 0) + 1, severity: v.severity });
  }
  return {
    errors: list.filter((v) => v.severity === 'ERROR'),
    warnings: list.filter((v) => v.severity === 'WARN'),
    byGate: Array.from(counts.entries()).map(([gate, x]) => ({ gate, ...x })),
  };
};

/** Mã cổng → câu tiếng Việt. Người duyệt không đọc code, đừng bắt họ đoán `ORPHAN_ENTITY` nghĩa là gì. */
const GATE_LABELS: Record<string, string> = {
  WORLD_PROPOSAL_PENDING: 'Bối cảnh chờ duyệt',   // 261015 P5a (D6): đề xuất KEEP/MAP chưa duyệt ⇒ writer đang bỏ bối cảnh
  ARC_REQUIRED: 'Nhân vật chính/phụ chưa nêu được đường thay đổi',
  SINGLE_USE_LOCATION: 'Bối cảnh chỉ dùng một tập mà chưa nêu cách tái dùng',
  BEAT_GAP: 'Có quãng dài không có cao trào nào',
  // 261007 — chi tiết gieo, hook, chỉ dẫn diễn
  BEAT_PLANT_MISSING: 'Cao trào lớn chưa có chi tiết gieo trước',
  BEAT_PLANT_AFTER_PAYOFF: 'Chi tiết gieo nằm sau cú nổ (tập gieo lớn hơn tập nổ)',
  BEAT_PLANT_SAME_CHAPTER: 'Chi tiết gieo cùng chương với cú nổ — gần như chưa gieo trước',
  BEAT_PLANT_NOT_IN_CHAPTER: 'Chi tiết gieo ghi là từ nguyên tác nhưng không thấy trong chương đã khai',
  BEAT_PLANT_UNCHECKED: 'Chưa kiểm được chi tiết gieo (khác ngôn ngữ nguồn)',
  // 261015 P2b-1
  SOURCE_ANCHOR_LOST: 'Trích nguồn đã neo không còn trong chương (chương đã sửa?)',
  // 261015 P3a — ý của tập chia (tóm tắt/móc/treo kèm trích)
  EPISODE_CLAIM_UNVERIFIED: 'Ý của tập chưa xác minh được (trích không có trong đoạn nguồn của tập)',
  EPISODE_HOOK_CLAIMS_INVALID: 'Tập có ≠ 1 ý «móc câu» — móc câu thành nhiều câu nối nhau hoặc trống',
  EPISODE_CLAIMS_STALE: 'Chữ tập đã sửa tay / qua trợ lý — chưa có trích nguồn',
  EPISODE_CLAIMS_LEGACY: 'Tập chia trước khi có «ý kèm trích» — chưa kiểm từng ý',
  // 261015 P4c-2b — nhịp hấp dẫn dự kiến của tập (thông báo, không phải lỗi)
  EPISODE_FLAT_RUN: 'Các tập liền nhau chưa chỉ ra nhịp hấp dẫn nào',
  EPISODE_APPEAL_UNCHECKED: 'Tập chưa khai nhịp hấp dẫn — chưa kiểm',
  // 261015 P4c-2c-1 (D4c) — Scene Plan chưa giao nhịp hấp dẫn đã gửi cho cảnh nào. THÔNG BÁO (BE `issueKind` = notice, không chip): chỉ nói cấu trúc plan — KHÔNG khẳng định nhịp có/không lên phim (việc của P4b + người đọc).
  APPEAL_NOT_PLANNED: 'Có nhịp hấp dẫn chưa được giao cho cảnh nào trong Scene Plan',
  BEAT_PLANT_CHAPTER_UNKNOWN: 'Chi tiết gieo khai ở chương không còn trong truyện',
  REQUIRED_MOMENT_EVIDENCE_WEAK: 'Khoảnh khắc bắt buộc chưa có trích nguồn xác minh được',
  ACCEPTANCE_EXPIRED: 'Chấp nhận cũ đã hết hiệu lực (nội dung đã đổi)',
  HOOK_ABSTRACT: 'Móc câu cuối tập còn trừu tượng — cần một chi tiết nhìn/nghe được',
  SUSPENSE_META: 'Điều còn treo nhắc tới "tập trước"/"chi tiết gieo" — nên viết như chuyện, không như ghi chú',
  MAJOR_BEAT: 'Cao trào lớn bị thiếu hoặc dồn hết vào tập cuối',
  BEAT_EPISODE_RANGE: 'Cao trào trỏ tới tập không tồn tại',
  EPISODE_COLUMNS: 'Có tập thiếu tóm tắt / móc câu / điều còn treo',
  NARRATIVE_VOICE: 'Có tập viết lời thoại — khung xương chỉ kể lại',
  // 261005 E5 — lint kịch bản
  SCRIPT_TOO_LONG: 'Thoại quá dài',
  THIN_VISUAL: 'Cảnh ít hình ảnh, hành động mỏng',
  CAST_UNKNOWN: 'Có nhân vật chưa khai trong khung xương',
  BEAT_MISSING: 'Thiếu một nhịp cần có từ khung xương',
  NO_DIALOGUE: 'Tập không có lời thoại',
  SUBTEXT_THIN: 'Tầng ngầm của lời thoại còn mỏng',
  CUE_TOO_LONG: 'Chỉ dẫn diễn (cue) quá dài',
  CUE_RATE_LOW: 'Rất ít lời thoại có chỉ dẫn diễn (cue)',
  CUE_LANGUAGE_MISMATCH: 'Chỉ dẫn diễn (cue) khác ngôn ngữ với lời thoại',
  CUE_ATTRIBUTION: 'Chỉ dẫn diễn (cue) ghi người khác nói thay vì cách nói',
  DIALOGUE_NO_SPEAKER: 'Có lời thoại không rõ ai nói',
  RELATIONSHIPS_SPARSE: 'Đồ thị quan hệ nhân vật còn quá thưa',
  CLIFFHANGER_ABSTRACT: 'Câu kết tập còn trừu tượng — cần một chi tiết nhìn/nghe được',
  SUBTEXT_RESTATES: 'Tầng ngầm chỉ nhắc lại lời thoại',
  END_NARRATOR_QUESTION: 'Đoạn kết là câu hỏi của người kể',
  END_NARRATOR_SUMMARY: 'Đoạn kết là lời tóm tắt của người kể',
  SCENE_NO_WANT: 'Cảnh chưa rõ nhân vật muốn gì',
  SCENE_METADATA_LOST: 'Cảnh mất thông tin cảnh (địa điểm/thời gian) — khôi phục được',
  LOCATION_UNKNOWN: 'Có bối cảnh chưa khai trong khung xương',
  EMPTY_SCRIPT: 'Kịch bản trống',
  RANGE_WRITER_NOT_SLICING: 'Bước viết chưa cắt theo đoạn nguồn',
  RANGE_OVER_EXISTING_DRAFTS: 'Các tập đã viết được giữ nguyên, chỉ chia phần sau',
  EMPTY_BEAT_TEXT: 'Có nhịp kịch bản bị trống chữ',
  ADAPTATION_BEAT_UNSOURCED: 'Nhịp thêm khi chuyển thể chưa có nguồn',
  BEAT_CHAPTER_SPLIT_ACROSS_EPISODES: 'Chương của cao trào trải qua nhiều tập — chưa biết rơi đúng tập nào (sẽ gắn neo nguồn ở bước sau)',
  BEAT_CHAPTER_UNCOVERED: 'Cao trào trỏ tới chương không tập nào phủ',
  CORE_MISSING: 'Thiếu tình tiết cốt lõi của nguyên tác',
  CRITIC: 'Điểm giám khảo AI',
  // 261015 P4b-1 (D13) — giám khảo C2 (khác `CRITIC` = điểm coverage của giám khảo cũ). `CRITIC_FINDING` = LỖI thật có nút trợ lý (cảnh có `refs`); `CRITIC_UNCHECKED` = chưa kiểm (BE issueKind `unchecked`, không đỏ).
  CRITIC_FINDING: 'Giám khảo AI thấy lỗi ở kịch bản',
  CRITIC_UNCHECKED: 'Giám khảo AI chưa kiểm được tập này',
  DIALOGUE_BLAND: 'Lời thoại nhạt, thiếu sức nặng',
  FAMOUS_NAME: 'Tên trùng nhân vật nổi tiếng',
  GATE_AI: 'Cổng AI báo vấn đề',
  JUNK_NAV: 'Chương lẫn chữ điều hướng của trang web',
  KEEP_SEPARATE_DROPPED: 'Cặp nhân vật cần tách đã không còn trong khung xương',
  MISSING_NAME: 'Thiếu tên',
  OUTLINE_STALE: 'Khung xương đã cũ so với nguồn',
  PLAN_OLD: 'Kịch bản cũ, chưa có Scene Plan',
  REQUIRED_MOMENT_UNPLACED: 'Khoảnh khắc bắt buộc chưa được đặt vào tập nào',
  SOURCE_NAME_LEAK: 'Tên gốc còn lọt vào kịch bản',
  SURNAME_GROUP_INVALID: 'Nhóm họ không hợp lệ',
  SURNAME_SPLIT: 'Cả nhà bị đổi họ không đồng nhất',
  TEXT_REVIEW: 'Tóm tắt / móc tập cần soát lại',
  VO_COPIES_PROSE: 'Lời kể chép lại nguyên văn truyện',
  VO_UNSOURCED_DROPPED: 'Lời kể không có nguồn bị bỏ',
  SOURCE_NAME_AUTO_SWAPPED: 'Đã tự thay tên gốc còn sót',
  // 261015 P4c-2c-2 (D6) — câu V.O. giới thiệu bị code gỡ/không đặt được. THÔNG BÁO (BE `issueKind` = notice), không phải lỗi.
  VO_INTRO_DROPPED: 'Đã bỏ câu giới thiệu không hợp lệ (đặt sai chỗ / vượt 2 câu hoặc 45 từ)',
  VO_INTRO_PLAN_MODE: 'Câu giới thiệu không đặt được vào cảnh nào',
  // 261015 P5d (D13) — code đã NẮN lệch nhân vật (thêm vào cảnh / chuyển nhịp của người ngoài cast thành hành động) thay vì bỏ cả tập. THÔNG BÁO (BE `issueKind` = notice), không phải lỗi.
  HEALED_CAST_MISMATCH: 'Code đã nắn lệch nhân vật trong cảnh (thay vì bỏ cả tập) — đọc lại các chỗ này',
  // 261005 E5 — bản địa hoá
  ALIAS_UNMAPPED: 'Còn tên chưa được ánh xạ',
  ALIAS_KEPT_PROPER: 'Tên riêng gốc bị giữ nguyên',
  LOCKED_OVERWRITTEN: 'Tên đã khóa bị thay đổi',
  NAME_COLLISION: 'Hai nhân vật bị đặt trùng tên',
  NAME_MISSING: 'Nhân vật chưa có tên bản địa',
  NAME_FROM_SOURCE: 'Tên còn giữ nguyên từ bản gốc',
  NAME_UNCHANGED: 'Tên chưa được đổi so với bản gốc',
  NAME_STOPWORDS: 'Tên chứa từ không phải tên riêng',
  NAME_HONORIFICS: 'Tên còn kèm danh xưng',
  NAME_ENDING_S: 'Tên kết thúc bằng “s” bất thường',
  NAME_PREFIX: 'Tên bị dính tiền tố',
  GENDER_DROP: 'Giới tính nhân vật bị mất sau khi đổi tên',
  IDENTITY_SAME_LOCALIZED_NAME: 'Hai thân xác cùng một ý thức bị đặt trùng tên',
  GLOSSARY_EMPTY: 'Chưa có từ vựng văn hóa cần ánh xạ',
  GLOSSARY_NAME_COLLISION: 'Từ vựng bị trùng tên nhân vật hoặc thuật ngữ khác',
  // 261023 E3-P2 — tên đích thường dùng cho giới kia (WARN, chạy cả dòng khoá)
  TARGET_NAME_GENDER_MISMATCH: 'Tên đích lệch giới nhân vật',
  ERA_UNSET: 'Chưa chọn thời đại cho truyện',
  ERA_AMBIGUOUS: 'Thời đại của truyện chưa rõ',
  UNKNOWN_REF: 'Có tập trỏ tới nhân vật hoặc bối cảnh chưa khai',
  ORPHAN_ENTITY: 'Có nhân vật hoặc bối cảnh khai ra nhưng không dùng ở tập nào',
  DUPLICATE_ID: 'Có mã bị trùng',
  EPISODE_COUNT: 'Danh sách tập không khớp số tập đã chốt',
  KEEP_REQUIRED: 'Chưa nêu giữ lại gì của nguyên tác',
  CUT_REQUIRED: 'Chưa nêu đã cắt bỏ những gì',
  CHANGE_LOG: 'Thiếu ghi chú nguồn gốc (gốc là ai / gộp từ ai)',
};

export const gateLabel = (gate: string): string => GATE_LABELS[gate] ?? gate;

/**
 * 261005 E5 — bỏ mã máy khỏi câu hiển thị: `char_x` / `loc_x` → tên thực thể (`nameOf`; không tra được ⇒ "một nhân vật"/"một bối cảnh"),
 * `beat_x` → "một nhịp", `[MÃ_GATE]` / `(MÃ_GATE)` → nhãn tiếng Việt (mã lạ ⇒ bỏ). Mã gốc chỉ còn ở "Chi tiết kỹ thuật".
 */
/**
 * F24 — ngày giờ gọn kiểu Việt: "07/10 06:50" (cùng năm) / "31/12/2025 23:59" (khác năm). Thay `toLocaleString()` mặc định
 * (kiểu Mỹ "10/7/2026, 6:50:41 AM" tuỳ máy). Rỗng / hỏng ⇒ "".
 */
export const formatShortDateTime = (iso: string | null | undefined, now: Date = new Date()): string => {
  const d = new Date(iso ?? '');
  if (!iso || Number.isNaN(d.getTime())) return '';
  const p2 = (x: number) => String(x).padStart(2, '0');
  const date = `${p2(d.getDate())}/${p2(d.getMonth() + 1)}${d.getFullYear() === now.getFullYear() ? '' : `/${d.getFullYear()}`}`;
  return `${date} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

export const humanizeCodes = (text: string, nameOf?: (id: string) => string): string => {
  const ent = (kind: string) => (id: string) => {
    const n = nameOf?.(id);
    return n && n !== id ? n : (kind === 'loc' ? 'một bối cảnh' : 'một nhân vật');
  };
  return String(text ?? '')
    .replace(/\b(char|character)_[A-Za-z0-9]+\b/g, (m) => ent('char')(m))
    .replace(/\b(loc|location)_[A-Za-z0-9]+\b/g, (m) => ent('loc')(m))
    .replace(/\bbeat_[A-Za-z0-9]+\b/g, 'một nhịp')
    .replace(/[[(]([A-Z][A-Z0-9_]{3,})[\])]/g, (_m, code: string) => (GATE_LABELS[code] ? `(${GATE_LABELS[code]})` : ''))
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const SCENE_PLAN_GATE_LABELS: Record<string, string> = {
  SCENE_PLAN_EMPTY: 'Scene Plan chưa có cảnh',
  SCENE_PLAN_ID_EMPTY: 'Cảnh thiếu mã Scene Plan',
  SCENE_PLAN_ID_DUPLICATE: 'Mã cảnh Scene Plan bị trùng',
  SCENE_PLAN_PURPOSE_EMPTY: 'Cảnh thiếu mục đích',
  SCENE_PLAN_WANT_EMPTY: 'Cảnh thiếu điều nhân vật muốn',
  SCENE_PLAN_OBSTACLE_EMPTY: 'Cảnh thiếu trở lực',
  SCENE_PLAN_TURN_EMPTY: 'Cảnh thiếu bước ngoặt',
  SCENE_PLAN_DELIVERY_MODE_INVALID: 'Cách kể của cảnh không hợp lệ',
  SCENE_PLAN_ORIGIN_INVALID: 'Nguồn gốc cảnh không hợp lệ',
  SCENE_PLAN_EVIDENCE_INVALID: 'Dữ liệu bằng chứng nguồn không hợp lệ',
  EVIDENCE_MISSING: 'Cảnh thiếu bằng chứng nguồn',
  EVIDENCE_CHAPTER_UNKNOWN: 'Bằng chứng trỏ sai chương nguồn',
  EVIDENCE_QUOTE_NOT_FOUND: 'Không tìm thấy đoạn trích trong chương nguồn',
  SOURCE_UNAVAILABLE: 'Tập chưa có chương nguồn để đối chiếu',
  ADAPTATION_REASON_MISSING: 'Cảnh thêm khi chuyển thể thiếu lý do',
  ADAPTATION_ADDED: 'Cảnh có vật liệu được thêm khi chuyển thể',
  // 261015 P2b-2 (N2): 5 mã khoảnh khắc ở ⑤ — trước đây lộ mã máy.
  REQUIRED_MOMENT_UNKNOWN: 'Cảnh khai khoảnh khắc không thuộc tập này',
  REQUIRED_MOMENT_MISMATCH: 'Cảnh khai khoảnh khắc nhưng không phủ đoạn nguồn của nó',
  REQUIRED_MOMENT_UNPROVEN: 'Khoảnh khắc chưa chứng minh được bằng nguồn',
  REQUIRED_MOMENT_UNCOVERED: 'Khoảnh khắc bắt buộc chưa nằm trong cảnh nào',
  REQUIRED_MOMENT_STALE: 'Kịch bản cũ so với nguồn — viết lại tập để kiểm khoảnh khắc',
  // 261015 P2b-3: chi tiết gieo thêm/sửa ở ② SAU khi tập gieo đã viết.
  PLANT_NOT_WRITTEN: 'Tập gieo chưa có chi tiết gieo — sửa một cảnh',
  PLANT_WRITE_UNCHECKED: 'Chưa kiểm được tập có nhận lời dặn gieo (không có biên lai lúc viết)',
};

export const scenePlanGateLabel = (gate: string): string => SCENE_PLAN_GATE_LABELS[gate] ?? gate;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const SCENE_PLAN_ORIGINS = new Set(['SOURCE_DIRECT', 'SOURCE_DERIVED', 'ADAPTATION_ADDED']);
const SCRIPT_DELIVERY_MODES = new Set(['VISUAL', 'DIALOGUE', 'VOICE_OVER', 'MIXED']);

const isEpisodeScenePlanItem = (item: unknown): item is EpisodeScenePlanItem => {
  if (!isRecord(item)) return false;
  if (!['id', 'locationId', 'purpose', 'want', 'obstacle', 'turn'].every((field) => typeof item[field] === 'string')) {
    return false;
  }
  if (!Array.isArray(item.characterIds) || !item.characterIds.every((id) => typeof id === 'string')) return false;
  if (!SCRIPT_DELIVERY_MODES.has(String(item.deliveryMode)) || !SCENE_PLAN_ORIGINS.has(String(item.origin))) return false;
  if (!Array.isArray(item.sourceEvidence)) return false;
  return item.sourceEvidence.every((evidence) => isRecord(evidence)
    && typeof evidence.chapter === 'number'
    && Number.isFinite(evidence.chapter)
    && typeof evidence.quote === 'string');
};

/** Không map theo vị trí: plan thiếu/trùng ID phải được coi là metadata không đáng tin. */
export const findScenePlanItem = (scenePlan: unknown, scenePlanId: unknown): EpisodeScenePlanItem | null => {
  if (!Array.isArray(scenePlan) || typeof scenePlanId !== 'string' || !scenePlanId.trim()) return null;
  const matches = scenePlan.filter((item) => isRecord(item) && item.id === scenePlanId);
  return matches.length === 1 && isEpisodeScenePlanItem(matches[0]) ? matches[0] : null;
};

export const isCurrentWriteScriptsRequest = (
  requestGeneration: number,
  currentGeneration: number,
  requestStoryId: string,
  currentStoryId: string,
): boolean => requestGeneration === currentGeneration && requestStoryId === currentStoryId;

export interface ScriptPlanSummary {
  scriptsWithPlan: number;
  legacyScripts: number;
  failedScripts: number;
  totalScenes: number;
  sourceDirect: number;
  sourceDerived: number;
  adaptationAdded: number;
  missingEvidence: number;
  sourceUnavailable: number;
  adaptationWarnings: number;
  evidenceIssues: number;
}

export const summarizeScriptPlans = (records: unknown): ScriptPlanSummary => {
  const summary: ScriptPlanSummary = {
    scriptsWithPlan: 0, legacyScripts: 0, failedScripts: 0, totalScenes: 0,
    sourceDirect: 0, sourceDerived: 0, adaptationAdded: 0,
    missingEvidence: 0, sourceUnavailable: 0, adaptationWarnings: 0, evidenceIssues: 0,
  };
  if (!Array.isArray(records)) return summary;

  for (const rawRecord of records) {
    if (!isRecord(rawRecord)) continue;
    if (rawRecord.status === 'FAILED' || (typeof rawRecord.error === 'string' && rawRecord.error.trim())) {
      summary.failedScripts++;
    }
    const script = isRecord(rawRecord.script) ? rawRecord.script : null;
    if (!script) continue;
    const plan = Array.isArray(script.scenePlan) ? script.scenePlan : null;
    if (!plan?.length) {
      summary.legacyScripts++;
      continue;
    }
    summary.scriptsWithPlan++;
    summary.totalScenes += plan.length;
    const violations = Array.isArray(rawRecord.violations) ? rawRecord.violations : [];
    const unavailable = violations.filter(
      (violation): violation is Record<string, unknown> => isRecord(violation) && violation.gate === 'SOURCE_UNAVAILABLE',
    );
    const unavailableRefs = new Set(
      unavailable.flatMap((violation) => Array.isArray(violation.refs)
        ? violation.refs.filter((ref: unknown): ref is string => typeof ref === 'string') : []),
    );
    for (const rawItem of plan) {
      if (!isRecord(rawItem)) continue;
      if (rawItem.origin === 'SOURCE_DIRECT') summary.sourceDirect++;
      if (rawItem.origin === 'SOURCE_DERIVED') summary.sourceDerived++;
      if (rawItem.origin === 'ADAPTATION_ADDED') summary.adaptationAdded++;
      if ((!Array.isArray(rawItem.sourceEvidence) || rawItem.sourceEvidence.length === 0)
        && !(typeof rawItem.id === 'string' && unavailableRefs.has(rawItem.id))) {
        summary.missingEvidence++;
      }
    }
    summary.sourceUnavailable += unavailable.length;
    summary.adaptationWarnings += violations.filter((v) => isRecord(v) && v.gate === 'ADAPTATION_ADDED').length;
    const evidenceViolations = violations.filter((v) => isRecord(v)
      && typeof v.gate === 'string'
      && ((v.gate.startsWith('EVIDENCE_') && v.gate !== 'EVIDENCE_MISSING')
        || v.gate === 'SCENE_PLAN_EVIDENCE_INVALID')).length;
    summary.evidenceIssues += evidenceViolations;
  }
  summary.evidenceIssues += summary.missingEvidence + summary.sourceUnavailable;
  return summary;
};

export interface EvidenceCellLine { key: 'gaps' | 'unchecked'; label: string; value: number; tone: 'defect' | 'unchecked' }

/**
 * 261015 P5a (D8) — ô "Vấn đề evidence" tách hai dòng, KHÔNG đổi `evidenceIssues` (tổng cũ = hai dòng cộng lại):
 * "Thiếu bằng chứng" (lỗi: cảnh thiếu trích + vi phạm EVIDENCE_* / SCENE_PLAN_EVIDENCE_INVALID) và
 * "Chưa kiểm được — nguồn không có" (`SOURCE_UNAVAILABLE`, kind `unchecked` ở BE ⇒ dáng chưa kiểm, xám). Đếm thuần FE — payload không có `kind` cho hai số này.
 */
export const evidenceCellLines = (s: Pick<ScriptPlanSummary, 'evidenceIssues' | 'sourceUnavailable'>): EvidenceCellLine[] => [
  { key: 'gaps', label: 'Thiếu bằng chứng', value: Math.max(0, s.evidenceIssues - s.sourceUnavailable), tone: 'defect' },
  { key: 'unchecked', label: 'Chưa kiểm được — nguồn không có', value: s.sourceUnavailable, tone: 'unchecked' },
];

/**
 * 261015 P5a (D8) — chữ " (N cảnh báo)" sau khi lưu sửa tay: CHỈ đếm lỗi thật (`kind` defect / thiếu kind). BE mang dòng CRITIC cũ
 * ("(chấm trước khi sửa tay)", kind `score`) và thông báo vào `warnings[]` — không phải cảnh báo mới do lần sửa này.
 */
export const manualSaveWarnNote = (warnings: ReadonlyArray<{ kind?: IssueKind }> | null | undefined): string => {
  const n = splitIssuesByKind(warnings).defects.length;
  return n > 0 ? ` (${n} cảnh báo)` : '';
};

export type FilmJobUiState = 'polling' | 'success' | 'failure' | 'unsupported';

export const classifyFilmJobStatus = (status: unknown): FilmJobUiState => {
  if (status === 'FILM_RUNNING') return 'polling';
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED') return 'failure';
  return 'unsupported';
};

export interface FilmWriteScriptsProjection {
  state: FilmJobUiState;
  total: number;
  skipped: number;
  written: number;
  failed: number;
  /** P4b: số tập chưa viết vì lô dừng sớm (outline range). */
  deferred?: number;
  stoppedReason?: string;
  message?: string;
}

export const projectFilmWriteScriptsTask = (task: unknown): FilmWriteScriptsProjection => {
  const taskRecord = isRecord(task) ? task : {};
  const detail = isRecord(taskRecord.detail) ? taskRecord.detail as unknown as FilmWriteScriptsTaskDetail : undefined;
  // Ưu tiên `progress` — ghi progressive nên có sớm hơn `result` (đợt 260826, Epic 2 Phase 3). `result`
  // là lưới đỡ cho dữ liệu cũ trước khi backend nối `onProgress` (Epic 2 Phase 1).
  const result = detail?.progress ?? detail?.result;
  return {
    state: classifyFilmJobStatus(taskRecord.status),
    total: Number(result?.total ?? 0),
    skipped: Number(result?.skipped ?? 0),
    written: Number(result?.written ?? 0),
    failed: Number(result?.failed ?? 0),
    deferred: Number(result?.deferred ?? 0),
    stoppedReason: result?.stoppedReason,
    message: detail?.error || detail?.note,
  };
};

/** Câu ngắn cho lúc ĐANG viết — khác câu "Viết xong..." dùng khi job đã kết thúc. */
export const describeScriptProgress = (p: FilmWriteScriptsProjection): string => {
  const parts = [`Đang viết ${p.written}/${p.total} tập`];
  if (p.failed > 0) parts.push(`hỏng ${p.failed}`);
  if (p.skipped > 0) parts.push(`bỏ qua ${p.skipped}`);
  return parts.join(' · ');
};

/** Khớp `HandoffResult` phía backend (`adminNovelHandoffService.ts`) — chỉ phần màn này đọc. */
export interface HandoffResultView {
  created?: number;
  updated?: number;
  skipped?: number;
  /** `kept`: 'plan' = chương đã có Film plan dựng từ nó · 'edited' = người đã sửa tay chương (E3-P5a) — cả hai KHÔNG bị ghi đè. */
  episodes?: Array<{ episode: number; chapterId?: string; kept?: 'plan' | 'edited' }>;
  /** 261015 P5a (D7): tên gốc còn rò trong chương vừa đẩy (server tính lại ở POST — có thể xuất hiện giữa GET plan và POST). KHÔNG chặn. */
  leaks?: Array<{ episode: number; terms: Array<{ term: string; count: number }> }>;
  /** 261023 E2-P2: tập mà chương vừa ghi đã tự thay tên gốc → tên mới (kịch bản ⑤ giữ nguyên). Chỉ có khi có ít nhất một tập thật sự đổi chữ chương. */
  autoSwap?: Array<{ episode: number; replaced: number; terms: Array<{ from: string; to: string; count: number }> }>;
}

/**
 * Câu báo kết quả handoff (E3-P5a F2). Tập có `kept` KHÔNG được cập nhật — phải nói ra, nếu không người dùng tưởng đã đẩy bản mới.
 * `warning` khi có tập bị giữ vì sửa tay (cần người quyết); còn lại `success`.
 */
export const describeHandoffResult = (r: HandoffResultView | null | undefined): { level: 'success' | 'warning'; text: string } => {
  const eps = r?.episodes ?? [];
  const list = (k: 'plan' | 'edited') => eps.filter((e) => e.kept === k).map((e) => e.episode).sort((a, b) => a - b).join(', ');
  const edited = list('edited');
  const plan = list('plan');
  const parts = ['Đã đẩy sang Chapters'];
  const counts: string[] = [];
  if (r?.created) counts.push(`tạo ${r.created}`);
  if (r?.updated) counts.push(`cập nhật ${r.updated}`);
  if (counts.length) parts[0] += ` (${counts.join(' · ')})`;
  if (edited) parts.push(`Giữ nguyên bản bạn đã sửa tay ở tập ${edited} — không ghi đè (sửa tay nếu muốn thay bằng bản mới)`);
  if (plan) parts.push(`Giữ nguyên tập ${plan} vì đã có Film plan dựng từ chương`);
  const leakLines = (r?.leaks ?? []).filter((l) => l && Array.isArray(l.terms) && l.terms.length)
    .map((l) => `tập ${l.episode} (${l.terms.map((t) => `${t.term} ×${t.count}`).join(', ')})`);
  // 261023 E2-P2: chỉ nêu TẬP (một tập có thể gộp nhiều term; không ghi tổng số lần). Không đổi `level`.
  const swapped = Array.from(new Set((r?.autoSwap ?? []).filter((a) => a && typeof a.episode === 'number').map((a) => a.episode))).sort((a, b) => a - b);
  if (swapped.length) parts.push(`Đã tự thay tên gốc trong chương tập ${swapped.join(', ')} (kịch bản ⑤ giữ nguyên)`);
  if (leakLines.length) parts.push(`Còn tên gốc rò trong chương đã đẩy: ${leakLines.join('; ')} — sửa bảng bí danh hoặc viết lại tập đó rồi đẩy lại`);
  return { level: edited || leakLines.length ? 'warning' : 'success', text: parts.join(' · ') };
};

/** Câu ngắn cho lúc đang nạp truyện. `!p` hoặc còn đọc mục lục → chưa có số chương để nói. */
export const describeCrawlProgress = (p: CrawlProgressData | null | undefined): string => {
  if (!p || p.phase === 'TOC') return 'Đang đọc mục lục…';
  const parts = [`Đã tải ${p.fetched}/${p.total} chương`];
  if (p.failed > 0) parts.push(`hỏng ${p.failed}`);
  if (p.skipped > 0) parts.push(`bỏ qua ${p.skipped}`);
  return parts.join(' · ');
};

/** Câu ngắn cho lúc đang quét truyện. */
export const describeScanProgress = (p: ScanProgressData | null | undefined): string => {
  if (!p) return 'Đang bắt đầu…';
  const parts = [`Đã quét ${p.done}/${p.total} quyển`];
  if (p.failed > 0) parts.push(`hỏng ${p.failed}`);
  if (p.skipped > 0) parts.push(`bỏ qua ${p.skipped}`);
  return parts.join(' · ');
};

export interface ApproveCheck {
  can: boolean;
  reason?: string;
}

export const canApprove = (
  outline: FilmOutline | null,
  violations: OutlineViolation[] | null | undefined,
): ApproveCheck => {
  if (!outline) return { can: false, reason: 'Chưa có khung xương' };
  const errors = (violations ?? []).filter((v) => v.severity === 'ERROR');
  if (errors.length) {
    const labels = Array.from(new Set(errors.map((e) => gateLabel(e.gate))));
    return { can: false, reason: `Còn ${errors.length} lỗi phải xử: ${labels.join(' · ')}` };
  }
  return { can: true };
};

export interface EpisodeBeatCell {
  ep: number;
  beats: OutlineBeat[];
  hasMajor: boolean;
}

/** Dải nhịp để vẽ thanh cao trào: mỗi tập một ô, biết tập nào có gì. */
export const beatsByEpisode = (beats: OutlineBeat[] | null | undefined, total: number): EpisodeBeatCell[] => {
  const n = Math.max(total, 0);
  const cells: EpisodeBeatCell[] = Array.from({ length: n }, (_, i) => ({ ep: i + 1, beats: [], hasMajor: false }));
  for (const b of beats ?? []) {
    const idx = b.episode - 1;
    if (idx < 0 || idx >= n) continue;
    cells[idx].beats.push(b);
    if (b.weight === 'major') cells[idx].hasMajor = true;
  }
  return cells;
};

/** Câu mô tả kết quả một lượt sửa — hiện thẳng cho người, kể cả phần bị từ chối. */
export const describePatchResult = (
  applied: Array<{ detail: string }>,
  rejected: Array<{ reason: string }>,
): string => {
  const parts: string[] = [];
  if (applied.length) parts.push(`Đã áp dụng ${applied.length} thay đổi`);
  if (rejected.length) parts.push(`${rejected.length} thao tác bị từ chối: ${rejected.map((r) => r.reason).join(' · ')}`);
  return parts.join('. ') || 'Không có thay đổi nào';
};

/**
 * Câu báo số lỗi/cảnh báo còn lại SAU một lượt sửa — để người dùng biết ngay kết quả mà không cần tự
 * cuộn lên đọc lại Card "Vi phạm" (đợt 260826, Epic 1 Phase 1).
 */
export const describeRemainingViolations = (errors: number, warnings: number): string => {
  if (errors === 0 && warnings === 0) return 'Không còn lỗi hay cảnh báo nào';
  if (errors > 0 && warnings === 0) return `Còn ${errors} lỗi`;
  if (errors === 0 && warnings > 0) return `Còn ${warnings} cảnh báo`;
  return `Còn ${errors} lỗi, ${warnings} cảnh báo`;
};

// ── Thanh tiến trình các bước của stage ⓪ ────────────────────────────────

export type StepState = 'done' | 'ready' | 'locked';

export interface AdaptationStep {
  key: 'source' | 'scan' | 'outline' | 'approve' | 'localize' | 'episodes' | 'scripts' | 'handoff';
  label: string;
  state: StepState;
  /** Câu mô tả hiện dưới nút — đang có gì, hoặc vì sao chưa mở được. */
  detail: string;
}

/** E3-P1 (Z3): đầu vào của bước Bản địa hoá — `localizeStepInput(view)` dựng từ `GET localize`. */
export interface LocalizeStepInput {
  /** `source_stories.localize_enabled`. */
  enabled: boolean;
  /** Có kết quả localize VÀ không lỗi thời so với chính sách hiện hành. */
  hasFreshResult: boolean;
  /** KEEP/MAP đã có bối cảnh ĐỀ XUẤT mà admin chưa duyệt. */
  worldPending: boolean;
}

/**
 * Dựng trạng thái các bước từ tiến độ thật.
 *
 * 🔴 Bước sau KHOÁ khi bước trước chưa xong, và luôn nói RÕ VÌ SAO khoá. Nút mờ không kèm lý do là
 * người dùng đứng nhìn không biết phải làm gì — đúng thứ khiến một luồng "chạy được" vẫn không dùng được.
 */
export const buildSteps = (p: AdaptationProgress | null, loc?: LocalizeStepInput): AdaptationStep[] => {
  const s = p ?? {
    rawChapters: 0, volumesDone: 0, volumesTotal: 0, hasOutline: false, outlineStatus: null,
    episodesPlanned: 0, episodesWritten: 0, scriptsWritten: 0, chapters: 0,
  };

  const hasSource = s.rawChapters > 0;
  const scanned = s.volumesDone > 0;
  const approved = s.outlineStatus === 'APPROVED';
  const hasEpisodes = s.episodesWritten > 0;
  const hasScripts = s.scriptsWritten > 0;
  // Z9: chỉ KỊCH BẢN đã viết mới kéo bước về done (bản copy nhập có sẵn phân tập vẫn phải localize); chờ duyệt bối cảnh ⇒ không bao giờ done.
  const localizeDone = !loc?.worldPending
    && (loc?.enabled === false || !!loc?.hasFreshResult || hasScripts);

  const step = (
    key: AdaptationStep['key'], label: string, done: boolean, ready: boolean, detail: string,
  ): AdaptationStep => ({ key, label, state: done ? 'done' : ready ? 'ready' : 'locked', detail });

  return [
    step('source', 'Nguồn truyện', hasSource, true,
      hasSource ? `${s.rawChapters} chương nguyên liệu` : 'Dán link để nạp, hoặc gõ tay từng chương'),

    step('scan', 'Quét truyện', scanned, hasSource,
      scanned ? `${s.volumesDone}/${s.volumesTotal} quyển đã quét`
        : hasSource ? 'Đọc cả truyện lấy tuyến, nhân vật, cao trào' : 'Cần có chương nguyên liệu trước'),

    step('outline', 'Dựng khung xương', s.hasOutline, scanned,
      s.hasOutline ? `${s.episodesPlanned} tập · nhân vật và bối cảnh đã gom`
        : scanned ? 'AI đề xuất cách cắt, gộp và chia tập' : 'Cần quét truyện trước'),

    step('approve', 'Duyệt khung xương', approved, s.hasOutline,
      approved ? 'Đã duyệt' : s.hasOutline ? 'Xem lại bên dưới rồi bấm duyệt' : 'Cần có khung xương trước'),

    // Z2: KHÔNG khoá bước sau; truyện đang dở (đã có phân tập/kịch bản) không bị kéo lùi.
    step('localize', 'Bản địa hoá', localizeDone, approved,
      loc?.worldPending ? 'Còn bối cảnh đề xuất chờ duyệt'
      : loc?.enabled === false ? 'Đang tắt cho truyện này'
        : loc?.hasFreshResult && !loc.worldPending ? 'Đã bản địa hoá'
          : hasScripts ? 'Bỏ qua (truyện đã có kịch bản)'
            : !approved ? 'Cần duyệt khung xương trước'
              : loc?.hasFreshResult ? 'Còn bối cảnh đề xuất chờ duyệt'
                : 'Chọn chính sách, chạy bản địa hoá, duyệt bối cảnh (khối Bản địa hoá bên dưới)'),

    step('episodes', 'Viết phân tập', hasEpisodes, approved,
      hasEpisodes ? `${s.episodesWritten}/${s.episodesPlanned} tập có tóm tắt`
        : approved ? 'Viết tóm tắt, móc câu và điều còn treo cho từng tập' : 'Cần duyệt khung xương trước'),

    step('scripts', 'Viết kịch bản', hasScripts, hasEpisodes,
      hasScripts ? `${s.scriptsWritten} tập đã có kịch bản`
        : hasEpisodes ? 'Viết cảnh, hành động và thoại cho từng tập' : 'Cần có phân tập trước'),

    step('handoff', 'Đẩy sang Chapters', s.chapters > 0 && s.chapters >= s.scriptsWritten, hasScripts,
      s.chapters > 0 && s.chapters >= s.scriptsWritten
        ? `${s.chapters} tập đã sang tab Chapters — làm phim được rồi`
        : hasScripts
          ? `Đưa ${s.scriptsWritten} tập đã viết sang tab Chapters${s.chapters > 0 ? ` (đang có ${s.chapters})` : ''}`
          : 'Cần có kịch bản trước'),
  ];
};

// ── Bản địa hoá sang phương Tây (Epic 8, đợt 260903) ─────────────────
// Logic thuần cho panel 2 — component `AdaptationLocalize.tsx` chỉ render + gọi API.

export const GLOSSARY_TYPE_LABELS: Record<FilmGlossaryType, string> = {
  HONORIFIC: 'Xưng hô',
  INSTITUTION: 'Thể chế',
  TITLE: 'Chức tước',
  CURRENCY: 'Tiền tệ',
  FOOD: 'Ẩm thực',
  ITEM: 'Vật phẩm',
  CUSTOM: 'Phong tục',
  LOCATION: 'Địa danh',
  ORGANIZATION: 'Tổ chức',
  OTHER: 'Khác',
};

export interface LocalizeSummary {
  charactersTotal: number;
  charactersNamed: number;
  locked: number;
  glossaryAuto: number;
  glossaryApproved: number;
  /** Đã chạy pass localize chưa — có ít nhất một nhân vật mang localizedName. */
  hasRun: boolean;
}

export const summarizeLocalize = (view: LocalizeView | null): LocalizeSummary => {
  const chars = view?.characters ?? [];
  const locs = view?.locations ?? [];
  const glossary = view?.glossary ?? [];
  const named = chars.filter((c) => !!c.localizedName).length;
  return {
    charactersTotal: chars.length,
    charactersNamed: named,
    locked: [...chars, ...locs].filter((e) => e.localizeMeta?.locked).length,
    glossaryAuto: glossary.filter((g) => g.status === 'AUTO').length,
    glossaryApproved: glossary.filter((g) => g.status === 'APPROVED').length,
    hasRun: named > 0,
  };
};

export interface AliasScanSummary {
  /** Đã chạy quét bí danh chưa. */
  hasRun: boolean;
  chapters: number;
  /** Tổng bí danh đã gán được cho các thực thể. */
  attached: number;
  /** Số thực thể chưa có bí danh nào — bước thay tên sẽ chỉ dựa vào tên khung xương. */
  entitiesWithout: number;
  /** Ứng viên chưa gán được, đã sắp theo số lần xuất hiện giảm dần. */
  unmapped: AliasCandidate[];
}

/**
 * Tóm tắt bảng bí danh cho panel 2 (E9-P1).
 *
 * 🔴 `unmapped` là con số đo được của cả cơ chế: nó thay cho niềm tin "chắc AI bắt đủ tên rồi".
 * Sắp theo `count` giảm dần vì tên xuất hiện 40 lần đáng xử trước tên xuất hiện 1 lần.
 */
export const summarizeAliasScan = (view: LocalizeView | null): AliasScanSummary => {
  const entities = [...(view?.characters ?? []), ...(view?.locations ?? [])];
  const scan = view?.aliasScan ?? null;
  return {
    hasRun: !!scan?.ranAt,
    chapters: scan?.chapters ?? 0,
    attached: entities.reduce((n, e) => n + (e.sourceAliases?.length ?? 0), 0),
    entitiesWithout: entities.filter((e) => !(e.sourceAliases?.length)).length,
    unmapped: [...(scan?.unmapped ?? [])].sort((a, b) => (b?.count ?? 0) - (a?.count ?? 0)),
  };
};

/**
 * Vi phạm của lượt localize gần nhất, gộp theo gate (E9-P3).
 *
 * 🔴 Trước đây chúng chỉ nằm trong `admin_tasks.detail.result` — lượt chạy còn ERROR sau retry thì outline
 * vẫn được ghi và người duyệt không có cách nào biết (TD-ADAPT-17).
 */
/**
 * Nhóm họ chỉ có nghĩa khi từ 2 người trở lên — id nhóm của một người đứng lẻ là nhiễu thuần tuý.
 *
 * 🔴 Người dùng báo 09/09: cột "Nhóm họ" hiện `davis`, `henderson`, `brooks`, `rick` cho bốn vai chức năng
 * mỗi người một nhóm ⇒ nhìn không hiểu để làm gì, mà `rick` còn là tên gọi chứ không phải họ.
 */
export const surnameGroupsCoNghia = (view: LocalizeView | null): Set<string> => {
  const dem = new Map<string, number>();
  for (const c of view?.characters ?? []) {
    const g = c.localizeMeta?.surnameGroup;
    if (g) dem.set(g, (dem.get(g) ?? 0) + 1);
  }
  return new Set(Array.from(dem.entries()).filter(([, n]) => n >= 2).map(([g]) => g));
};

export interface LocalizeIssueGroup {
  gate: string;
  severity: 'ERROR' | 'WARN';
  count: number;
  message: string;
}

const groupLocalizeList = (list: OutlineViolation[]): LocalizeIssueGroup[] => {
  const byGate = new Map<string, LocalizeIssueGroup>();
  for (const v of list) {
    if (!v?.gate) continue;
    const cur = byGate.get(v.gate);
    if (cur) { cur.count += 1; continue; }
    byGate.set(v.gate, {
      gate: v.gate,
      severity: v.severity === 'ERROR' ? 'ERROR' : 'WARN',
      count: 1,
      message: v.message ?? '',
    });
  }
  // ERROR trước, rồi tới gate xuất hiện nhiều nhất — người duyệt xử thứ nặng và thứ lặp trước.
  return Array.from(byGate.values()).sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'ERROR' ? -1 : 1;
    return b.count - a.count || a.gate.localeCompare(b.gate);
  });
};

/** 261015 P1: CHỈ lỗi thật (`defect`; thiếu `kind` ⇒ defect) — dải cảnh báo ③. Thông báo/điểm/… xem `groupLocalizeOthers`. */
export const groupLocalizeIssues = (view: LocalizeView | null): LocalizeIssueGroup[] =>
  groupLocalizeList(splitIssuesByKind(view?.localizeMeta?.violations ?? []).defects);

export interface LocalizeOtherGroup extends LocalizeIssueGroup { kind: OtherIssueKind }

/** 261015 P1: phần KHÔNG phải lỗi của ③ (khối gập "Thông báo & điểm"); `retired` đã bị bỏ. */
export const groupLocalizeOthers = (view: LocalizeView | null): LocalizeOtherGroup[] => {
  const { others } = splitIssuesByKind(view?.localizeMeta?.violations ?? []);
  return (['score', 'notice', 'unchecked', 'stale'] as OtherIssueKind[])
    .flatMap((kind) => groupLocalizeList(others[kind]).map((g) => ({ ...g, kind })));
};

/** Quan hệ RÀNG cùng họ — khớp `filmLocalizeLint` backend (KHÔNG spouse, KHÔNG faction organization). */
const BLOOD_TYPES = new Set(['parent-child', 'sibling', 'kin']);

/** Họ = token cuối của tên đã localize (bỏ hậu tố Jr/Sr/III…). */
const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);
export const surnameOfLocalized = (name?: string | null): string => {
  const toks = String(name ?? '')
    .toLowerCase().replace(/[.'’`-]/g, ' ').replace(/\s+/g, ' ').trim()
    .split(' ').filter(Boolean);
  while (toks.length > 1 && SUFFIXES.has(toks[toks.length - 1])) toks.pop();
  return toks.length ? toks[toks.length - 1] : '';
};

export interface SurnameGroup {
  key: string;
  members: Array<{ id: string; name: string; localizedName: string | null; surname: string }>;
  /** Có ≥2 họ khác nhau trong nhóm (dòng nên cùng họ mà lệch) — chỉ để CẢNH BÁO, backend lint mới là cổng. */
  split: boolean;
}

/**
 * Các nhóm nhân vật NÊN cùng họ: `surnameGroup` do AI đặt · faction `kind='family'` · huyết thống.
 * Trả kèm cờ `split` để bảng tô cảnh báo dòng lệch họ.
 */
export const localizeSurnameGroups = (view: LocalizeView | null): SurnameGroup[] => {
  if (!view) return [];
  const nameOf = new Map(view.characters.map((c) => [c.id, c]));
  const groups = new Map<string, Set<string>>();
  const add = (key: string, id: string) => {
    if (!key || !nameOf.has(id)) return;
    if (!groups.has(key)) groups.set(key, new Set());
    groups.get(key)!.add(id);
  };
  for (const c of view.characters) {
    if (c.localizeMeta?.surnameGroup) add(`sg:${c.localizeMeta.surnameGroup.toLowerCase()}`, c.id);
  }
  for (const f of view.factions) {
    if (f.kind !== 'family') continue;
    for (const m of f.memberIds) add(`fac:${f.id}`, m);
  }
  for (const r of view.relationships) {
    if (!BLOOD_TYPES.has(r.type)) continue;
    const k = `rel:${[r.a, r.b].sort().join('|')}`;
    add(k, r.a);
    add(k, r.b);
  }
  const out: SurnameGroup[] = [];
  Array.from(groups.entries()).forEach(([key, ids]) => {
    if (ids.size < 2) return;
    const members = Array.from(ids).map((id) => {
      const c = nameOf.get(id) as LocalizeView['characters'][number];
      return { id, name: c.name, localizedName: c.localizedName, surname: surnameOfLocalized(c.localizedName) };
    });
    const surnames = new Set(members.map((m) => m.surname).filter(Boolean));
    out.push({ key, members, split: surnames.size > 1 });
  });
  return out;
};

/** Set id nhân vật thuộc một nhóm `split` — để component tra nhanh khi render bảng. */
export const splitCharacterIds = (view: LocalizeView | null): Set<string> => {
  const ids = new Set<string>();
  localizeSurnameGroups(view).forEach((g) => {
    if (g.split) g.members.forEach((m) => ids.add(m.id));
  });
  return ids;
};


// ── Epic 3 / Phase 1 (260928): chia tập theo đoạn nguồn · chính sách localize · nối tập ─────────────

/** Dựng đầu vào bước Bản địa hoá từ `GET localize`. `null` (chưa tải) ⇒ `undefined` để `buildSteps` dùng đường cũ. */
export const localizeStepInput = (view: LocalizeView | null | undefined): LocalizeStepInput | undefined => {
  if (!view) return undefined;
  // Legacy: outline chưa có chính sách ⇒ đọc như WESTERNIZE ⇒ không có bước duyệt bối cảnh đề xuất.
  const mode: LocalizeMode = view.policy?.mode ?? 'WESTERNIZE';
  return {
    enabled: view.localizeEnabled !== false,
    hasFreshResult: !!view.localizeMeta?.ranAt && !view.resultStale,
    worldPending: mode !== 'WESTERNIZE' && view.worldSettingStatus === 'PROPOSED',
  };
};

/** Nhãn đoạn nguồn của tập — FE không biết độ dài chương nên chỉ ghi offset (Z6). */
export const formatSourceRanges = (ranges: SourceRange[] | undefined | null): string =>
  (ranges ?? []).map((r) => `Chương ${r.orderIndex} [${r.startOffset}–${r.endOffset}]`).join(', ');

export type EpisodeSplitMode = 'chapter' | 'range';

/** Body của `POST …/episodes`. Chế độ chương gửi ĐÚNG body cũ `{}` (Z8). */
export const buildEpisodesBody = (mode: EpisodeSplitMode): { sourceMode?: 'range' } =>
  mode === 'range' ? { sourceMode: 'range' } : {};

/**
 * Câu cho kết quả lô viết dừng sớm (`stoppedReason` = `FAILED:<tập>` | `GATE:<tập>` | `EPISODE_PLAN_CHANGED` | 261015 P4c-2c-1: `OVERVIEW_NOT_EFFECTIVE` | `OVERVIEW_CHANGED`). `null` = không có gì để nói.
 * Hai mã tổng quan KHÔNG kèm số tập (cả lô dừng, tập bị chặn không ghi gì — không phải "lỗi" của tập); append-run sau lô như vậy vẫn RUNNING + ghi lỗi (Q3), không DONE.
 */
export const describeBatchStop = (
  r: { deferred?: number; stoppedReason?: string } | null | undefined,
): string | null => {
  const deferred = Number(r?.deferred ?? 0);
  const reason = r?.stoppedReason ?? '';
  if (r && (reason === 'OVERVIEW_NOT_EFFECTIVE' || reason === 'OVERVIEW_CHANGED')) {
    const why = reason === 'OVERVIEW_CHANGED' ? 'tổng quan truyện vừa đổi' : 'tổng quan truyện chưa chốt hoặc đã cũ';
    return `Lô dừng vì ${why}${deferred > 0 ? ` — ${deferred} tập chưa viết` : ''}; chưa ghi gì cho tập bị chặn, mở Tổng quan truyện ở ② kiểm tra rồi viết lại`;
  }
  if (!r || deferred <= 0) return null;
  const m = /^(FAILED|GATE):(\d+)$/.exec(reason);
  const tail = `${deferred} tập sau đang chờ`;
  if (!m) return `Lô dừng sớm — ${tail}`;
  const why = m[1] === 'FAILED' ? 'lỗi' : 'cổng nối tập chặn';
  return `Dừng ở tập ${m[2]} (${why}) — ${tail}`;
};

/**
 * 261015 P4c-2c-1 (F1 review FE) — lô viết dừng vì tổng quan (`stoppedReason` = `OVERVIEW_NOT_EFFECTIVE` | `OVERVIEW_CHANGED`) ⇒ dải thông báo kèm lối ra "Mở Tổng quan truyện";
 * dừng vì lý do khác (FAILED/GATE/EPISODE_PLAN_CHANGED/không dừng) ⇒ `null`. Luật cứng: mỗi `OVERVIEW_*` phải có lối ra, kể cả kết cục job (không chỉ 409 lúc khởi động).
 */
export const batchStopExit = (r: { stoppedReason?: string } | null | undefined): 'openOverview' | null =>
  r && (r.stoppedReason === 'OVERVIEW_NOT_EFFECTIVE' || r.stoppedReason === 'OVERVIEW_CHANGED') ? 'openOverview' : null;

/** Body `PUT …/localize/:storyId/policy` + kiểm phía client (MAP cần văn hoá đích 2–60 ký tự). */
export const buildPolicyBody = (
  mode: LocalizeMode, targetCulture: string, force?: boolean,
): { ok: true; body: { mode: LocalizeMode; targetCulture?: string; force?: true } } | { ok: false; reason: string } => {
  const culture = targetCulture.trim();
  if (mode === 'MAP' && (culture.length < 2 || culture.length > 60)) {
    return { ok: false, reason: 'Văn hoá đích cần 2–60 ký tự (vd: Arabian, Nordic, Japanese).' };
  }
  return { ok: true, body: { mode, ...(mode === 'MAP' ? { targetCulture: culture } : {}), ...(force ? { force: true as const } : {}) } };
};

// ── Lỗi range / localize → tiếng Việt ───────────────────────────────────────────────────────────

const CONTINUITY_REASON_LABELS: Record<string, string> = {
  BOOTSTRAP_PRE_P4B: 'viết trước khi có nối tập',
  ANCESTOR_STALE: 'tập trước đó trong chuỗi cần viết lại',
  PREDECESSOR_CHANGED: 'tập ngay trước đã viết lại',
  ENTRY_MISSING: 'thiếu dấu nối với tập trước',
  PREDECESSOR_MISSING: 'chưa có tập ngay trước',
  PREDECESSOR_NOT_DRAFT: 'tập ngay trước chưa viết xong (hỏng/thiếu)',
  NO_DRAFT: 'chưa có tập nào được viết',
  GAP_NOT_DRAFT: 'có tập bị hổng ở giữa (chưa viết/hỏng)',
  EPISODE_NOT_IN_OUTLINE: 'tập không còn trong khung xương',
  SOURCE_BINDING_MISSING: 'thiếu biên lai đoạn nguồn',
  CONTENT_CHANGED: 'nội dung chương nguồn đã đổi',
  CHAPTER_MISSING: 'chương nguồn không còn',
  RANGES_CHANGED: 'đoạn nguồn của tập đã đổi',
  POLICY_CHANGED: 'chính sách localize đã đổi',
};

export const describeContinuityReason = (code: string): string => CONTINUITY_REASON_LABELS[code] ?? code;

export interface AdaptationErrorView {
  title: string;
  lines: string[];
  /** Có ⇒ hiện nút "Viết lại tuần tự từ tập X". */
  recoverFrom?: number;
  /** Có ⇒ hỏi xác nhận rồi gửi lại với `force:true` (LOCALIZE_POLICY_LOCKED, WORLD_SETTING_PINNED). */
  forceable?: boolean;
  code?: string;
  /** 261015 P4c-2c-1 (D10) — `openOverview` ⇒ hộp lỗi hiện nút "Mở Tổng quan truyện" (OVERVIEW_NOT_EFFECTIVE / OVERVIEW_CHANGED). */
  exit?: 'openOverview';
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const SIMPLE_ERRORS: Record<string, { title: string; hint?: string; forceable?: boolean }> = {
  SOURCE_RANGE_STALE: {
    title: 'Đoạn nguồn không còn khớp chương hiện tại',
    hint: 'Chương nguồn bị sửa/xoá sau khi chia tập. Chia tập lại theo đoạn nguồn (không tự chia lại tập đã viết).',
  },
  RANGE_DUPLICATE_ORDER_INDEX: {
    title: 'Hai chương khác nhau trùng số thứ tự trong cùng một tập',
    hint: 'Không tách được bằng chứng theo số chương — sửa lại số thứ tự chương nguồn rồi chia tập lại.',
  },
  RANGE_NOVEL_CONTEXT_UNSUPPORTED: {
    title: 'Chia theo đoạn nguồn không dùng được với novelContext',
    hint: 'Bản nén/chương liên quan có thể lộ chữ sau điểm cắt — bỏ novelContext hoặc chia lại theo chương.',
  },
  LOCALIZE_INCOMPLETE: {
    title: 'Bản địa hoá chưa đủ — thiếu tên/term bắt buộc',
    hint: 'Chạy lại bản địa hoá (Bồi chỗ trống) hoặc điền tay các tên còn thiếu.',
  },
  LOCALIZE_POLICY_LOCKED: {
    title: 'Đã có kết quả bản địa hoá — đổi chính sách sẽ làm kết quả cũ lỗi thời',
    hint: 'Xác nhận để đổi; sau đó chạy lại bản địa hoá (Làm lại từ đầu).',
    forceable: true,
  },
  LOCALIZE_JOB_RUNNING: { title: 'Bản địa hoá đang chạy ở nền', hint: 'Đợi job xong rồi thử lại.' },
  EPISODES_STALE: {
    title: 'Danh sách tập đã cũ — chưa đẩy sang Chapters được',
    hint: 'Khung xương đã gộp thêm nhân vật/bối cảnh/chương sau khi chia tập. Vào bước Chia tập, chia lại rồi đẩy.',
  },
  OUTLINE_CHANGED: { title: 'Khung xương vừa bị đổi ở nơi khác', hint: 'Bấm Tải lại rồi làm lại thao tác.' },
  NO_PROPOSAL: { title: 'Chưa có bối cảnh đề xuất để duyệt', hint: 'Chạy bản địa hoá (KEEP/MAP) để AI đề xuất bối cảnh trước.' },
  PROPOSAL_STALE: {
    title: 'Bối cảnh đề xuất thuộc chính sách cũ',
    hint: 'Chạy lại bản địa hoá (Làm lại từ đầu) để có đề xuất mới.',
  },
  WORLD_SETTING_PINNED: {
    title: 'Bối cảnh của truyện đang được ghim',
    hint: 'Duyệt sẽ ghi đè bối cảnh đã ghim — xác nhận nếu đúng ý.',
    forceable: true,
  },
  POLICY_CULTURE_REQUIRED: { title: 'Chính sách Ánh xạ cần văn hoá đích', hint: 'Nhập văn hoá đích 2–60 ký tự.' },
  POLICY_CULTURE_INVALID: { title: 'Văn hoá đích không hợp lệ', hint: 'Cần 2–60 ký tự.' },
  POLICY_MODE_INVALID: { title: 'Chế độ chính sách không hợp lệ' },
  OUTLINE_NOT_FOUND: { title: 'Chưa có khung xương cho truyện này' },
  OUTLINE_MISSING: { title: 'Chưa có khung xương', hint: 'Dựng khung xương trước.' },
  OUTLINE_NOT_APPROVED: { title: 'Khung xương chưa được duyệt', hint: 'Duyệt khung xương rồi chia tập.' },
  EPISODES_NOT_SET: { title: 'Khung xương chưa chốt số tập' },
  EPISODES_BEYOND_CHAPTER_UNITS: {
    title: 'Số tập vượt quá số chương khả dụng (chế độ Theo chương)',
    hint: 'Thử chế độ Theo đoạn nguồn.',
  },
  // 260928 E3-P2 — cổng AI review (override / rejudge). STEP_GATE_BLOCKED có nhánh riêng (đọc `details`).
  GATE_NOT_BLOCKED: { title: 'Cổng này không ở trạng thái BLOCKED', hint: 'Không còn gì để vượt — bấm Tải lại.' },
  OVERRIDE_REASON_REQUIRED: { title: 'Cần ghi lý do vượt cổng', hint: 'Lý do tối thiểu 5 ký tự — sẽ được lưu vào kiểm toán.' },
  STEP_NOT_RUN: { title: 'Bước này chưa chạy — chưa có gì để chấm', hint: 'Chạy bước đó trước.' },
};

/**
 * 261015 P4c-2b (D9b) + P4c-2c-1 (D10) — truyện CÓ tổng quan: `OVERVIEW_NOT_EFFECTIVE` (chưa chốt / đã cũ ⇒ BE từ chối chốt dàn tập / chia / viết / sửa cảnh bằng AI TRƯỚC khi gọi AI) và `OVERVIEW_CHANGED`
 * (đổi trong lúc viết / sau khi xem thử ⇒ không ghi gì). Cả hai: 0 AI hoặc 0 ghi + lối ra "Mở Tổng quan truyện". Chữ + nguyên nhân từng dòng lấy từ `overviewErrorView` (một nguồn với ④ và Trợ lý).
 */
const OVERVIEW_WRITER_TITLE: Record<'OVERVIEW_NOT_EFFECTIVE' | 'OVERVIEW_CHANGED', string> = {
  OVERVIEW_NOT_EFFECTIVE: 'Tổng quan truyện chưa chốt hoặc đã cũ',
  OVERVIEW_CHANGED: 'Tổng quan truyện vừa đổi',
};
/**
 * Dòng đầu (sau tiêu đề) — 🔴 KHÔNG được lặp lại tiêu đề: hộp lỗi in tiêu đề rồi dòng đầu, các toast `title — lines[0]` (AdaptationGateCard / SeasonReaderCard) ghép cả hai (F3 review FE).
 * Câu cho ⑤ (viết tập / xem thử / áp khối), khác câu chung `OVERVIEW_NOT_EFFECTIVE_TEXT` của ④ (vốn bắt đầu bằng đúng tiêu đề).
 */
const OVERVIEW_WRITER_BODY: Record<'OVERVIEW_NOT_EFFECTIVE' | 'OVERVIEW_CHANGED', string> = {
  OVERVIEW_NOT_EFFECTIVE: 'Hãy chốt lại tổng quan ở ② rồi viết hoặc xem thử lại. Chưa có gì được ghi, chưa tốn AI.',
  OVERVIEW_CHANGED: 'Tổng quan đã đổi trong lúc viết hoặc sau khi xem thử. Chưa có gì được ghi — kiểm tra lại tổng quan (chốt lại nếu cần) rồi viết hoặc xem thử lại.',
};

const GATE_STEP_LABEL: Record<string, string> = { localize: 'Bản địa hoá', episode_split: 'Chia tập', season_reader: 'AI đọc cả mùa' };
const MAX_GATE_ISSUE_LINES = 5;

/**
 * Lỗi axios của route chia tập / viết kịch bản / handoff / localize policy / accept-world ⇒ nội dung hiện cho người dùng.
 * Mã lạ ⇒ hiện nguyên `message` của server. Hàm thuần: nhận `unknown` vì `catch` của TS không có kiểu.
 */
export const describeAdaptationError = (err: unknown): AdaptationErrorView => {
  const resp = isRecord(err) && isRecord(err.response) ? err.response : null;
  const status = resp && isNum(resp.status) ? resp.status : undefined;
  const data = resp && isRecord(resp.data) ? resp.data : null;
  const code = typeof data?.code === 'string' ? data.code : undefined;
  const message = typeof data?.message === 'string' ? redactMoney(data.message) : '';
  const details = isRecord(data?.details) ? data!.details as Record<string, unknown> : null;

  if (!resp) return { title: 'Không kết nối được máy chủ', lines: ['Kiểm tra mạng rồi thử lại.'] };

  if (code === 'RANGE_CONTINUITY_MISSING') {
    const dirty = isNum(details?.firstDirty) ? details!.firstDirty as number : undefined;
    const reasons = Array.isArray(details?.reasons) ? (details!.reasons as unknown[]).map(String) : [];
    const lines = [dirty !== undefined
      ? `Tập ${dirty} chưa sẵn sàng làm tập trước của tập kế: ${reasons.map(describeContinuityReason).join('; ') || 'thiếu'}.`
      : message];
    return { title: 'Chưa viết được — chuỗi nối tập đang bẩn', lines, recoverFrom: dirty, code };
  }

  if (code === 'RANGE_HANDOFF_NOT_READY') {
    const eps = (Array.isArray(details?.episodes) ? details!.episodes as unknown[] : [])
      .filter((e): e is Record<string, unknown> => isRecord(e) && isNum(e.episode))
      .sort((a, b) => (a.episode as number) - (b.episode as number));
    if (!eps.length) return { title: 'Chưa đẩy được sang Chapters — còn tập chưa sẵn sàng', lines: [message], code };
    const lines = eps.map((e) => {
      const rs = (Array.isArray(e.reasons) ? e.reasons : []).map(String);
      return `Tập ${e.episode}: ${rs.map(describeContinuityReason).join('; ') || 'chưa sẵn sàng'}`;
    });
    // NO_DRAFT = chưa có tập nào ⇒ không có gì để "viết lại".
    const recoverable = eps.filter((e) => !(Array.isArray(e.reasons) ? e.reasons : []).includes('NO_DRAFT'));
    const recoverFrom = recoverable.length ? recoverable[0].episode as number : undefined;
    return { title: 'Chưa đẩy được sang Chapters — còn tập chưa sẵn sàng', lines, recoverFrom, code };
  }

  // 261023 E2-P2: chương sắp đẩy còn tên gốc mà bảng tên mới không thay được — liệt kê tập + term, chỉ đường sửa (sửa tay ở ⑤ hoặc đổi tên mới ở ③).
  if (code === 'SOURCE_NAME_UNRESOLVED') {
    const eps = (Array.isArray(details?.episodes) ? details!.episodes as unknown[] : [])
      .filter((e): e is Record<string, unknown> => isRecord(e) && isNum(e.episode))
      .sort((a, b) => (a.episode as number) - (b.episode as number));
    const termsOf = (e: Record<string, unknown>): string => (Array.isArray(e.terms) ? e.terms as unknown[] : [])
      .filter((t): t is Record<string, unknown> => isRecord(t) && typeof t.term === 'string')
      .map((t) => `${t.term}${isNum(t.count) && t.count > 1 ? ` ×${t.count}` : ''}`).join(', ');
    const lines = eps.length ? eps.map((e) => `Tập ${e.episode}: ${termsOf(e) || 'còn tên gốc'}`) : [message].filter(Boolean);
    return {
      title: 'Chưa đẩy được sang Chapters — còn tên gốc không tự thay được',
      lines: [...lines, 'Mở tập ở ⑤ sửa tay chỗ đó, hoặc đổi tên mới ở ③, rồi đẩy lại.'],
      code,
    };
  }

  // 261015 P5d (D2): cổng handoff chặn tập mà giám khảo AI còn thấy lỗi nặng (CRITICAL) và bản sửa không đạt — liệt kê tập, chỉ đường sửa (không có nút viết lại tuần tự: sửa cảnh lỗi cũng gỡ được chặn).
  if (code === 'CRITIC_UNRESOLVED') {
    const eps = (Array.isArray(details?.episodes) ? details!.episodes as unknown[] : [])
      .filter((e): e is Record<string, unknown> => isRecord(e) && isNum(e.episode))
      .sort((a, b) => (a.episode as number) - (b.episode as number));
    const lines = eps.length
      ? eps.map((e) => `Tập ${e.episode}: còn ${isNum(e.criticalCount) ? e.criticalCount : 'ít nhất 1'} lỗi nặng (CRITICAL) của giám khảo AI`)
      : [message].filter(Boolean);
    return {
      title: 'Chưa đẩy được sang Chapters — giám khảo AI còn thấy lỗi nặng chưa sửa',
      lines: [...lines, 'Mở các tập trên: sửa cảnh lỗi ("Nhờ AI sửa" hoặc sửa tay) hoặc viết lại tập, rồi đẩy lại.'],
      code,
    };
  }

  // 260928 E3-P3 (K7/K9): cổng "AI đọc cả mùa" ở handoff range — chỉ đường về khối "AI đọc cả mùa".
  if (code === 'SEASON_READER_REQUIRED') {
    const covered = isNum(details?.coveredTo) ? details!.coveredTo as number : undefined;
    const draftMax = isNum(details?.draftMax) ? details!.draftMax as number : undefined;
    const lines = covered !== undefined && draftMax !== undefined
      ? [`AI mới đọc tới tập ${covered}, nhưng đã có kịch bản tới tập ${draftMax}.`]
      : ['Chưa có kết quả "AI đọc cả mùa" cho kịch bản hiện tại.'];
    return { title: 'Cần cho AI đọc cả mùa trước khi đẩy sang Chapters', lines: [...lines, 'Bấm "AI đọc cả mùa" ở khối bên dưới bước Đẩy sang Chapters rồi thử lại.'], code };
  }
  if (code === 'SEASON_READER_RUNNING') {
    return { title: 'AI đang đọc cả mùa', lines: ['"AI đọc cả mùa" đang chạy ở nền — chờ xong (vài phút) rồi thử đẩy sang Chapters lại.'], code };
  }
  if (code === 'SEASON_READER_STALE') {
    const reasons = (Array.isArray(details?.reasons) ? details!.reasons as unknown[] : []).map(String).slice(0, 4);
    return {
      title: 'Kết quả AI đọc cả mùa đã cũ',
      lines: [...(reasons.length ? reasons : [message].filter(Boolean)), 'Kịch bản hoặc bible đã đổi từ lần AI đọc — bấm "AI đọc lại" ở khối "AI đọc cả mùa" rồi thử lại.'],
      code,
    };
  }

  if (code === 'STEP_GATE_BLOCKED' && details?.stepKey === 'season_reader') {
    const issues = (Array.isArray(details?.blockingIssues) ? details!.blockingIssues as unknown[] : []).filter(isRecord);
    const rows = issues.slice(0, MAX_GATE_ISSUE_LINES).map((i) => {
      const where = issueWhere({ unit: typeof i.unit === 'string' ? i.unit : '', evidence: [] });
      return `${where}: ${typeof i.problem === 'string' ? i.problem : ''}`.trim();
    });
    const more = issues.length > MAX_GATE_ISSUE_LINES ? [`… và ${issues.length - MAX_GATE_ISSUE_LINES} lỗi nữa — xem khối "AI đọc cả mùa".`] : [];
    const eps = issues.map((i) => /^ep:(\d+)$/.exec(typeof i.unit === 'string' ? i.unit : '')).filter((m): m is RegExpExecArray => !!m).map((m) => Number(m[1]));
    return {
      title: 'Cổng "AI đọc cả mùa" đang chặn handoff',
      lines: [
        issues.length ? `AI đọc cả mùa thấy ${issues.length} lỗi liên tập khách quan:` : (message || 'AI đọc cả mùa đang chặn.'),
        ...rows, ...more,
        'Viết lại tập có lỗi (rồi "AI đọc lại"), hoặc bấm "Vẫn tiếp tục" kèm lý do ở khối "AI đọc cả mùa".',
      ],
      recoverFrom: eps.length ? Math.min(...eps) : undefined,
      code,
    };
  }

  if (code === 'STEP_GATE_BLOCKED') {
    const blockedSteps = (Array.isArray(details?.blockedSteps) ? details!.blockedSteps as unknown[] : [details?.stepKey])
      .filter((x): x is string => typeof x === 'string');
    const stepNames = blockedSteps.map((k) => `"${GATE_STEP_LABEL[k] ?? k}"`).join(' và ');
    const issues = (Array.isArray(details?.blockingIssues) ? details!.blockingIssues as unknown[] : []).filter(isRecord);
    const head = issues.length
      ? `Cổng AI của bước ${stepNames || 'trước'} đang chặn: ${issues.length} lỗi khách quan.`
      : (message || `Cổng AI của bước ${stepNames || 'trước'} đang chặn.`);
    const rows = issues.slice(0, MAX_GATE_ISSUE_LINES).map((i) => {
      const where = issueWhere({ unit: typeof i.unit === 'string' ? i.unit : '', evidence: Array.isArray(i.evidence) ? i.evidence as never[] : [] });
      return `${where}: ${typeof i.problem === 'string' ? i.problem : ''}`.trim();
    });
    const more = issues.length > MAX_GATE_ISSUE_LINES ? [`… và ${issues.length - MAX_GATE_ISSUE_LINES} lỗi nữa — xem khối "Cổng AI" của bước đó.`] : [];
    return {
      title: 'Cổng AI đang chặn bước này',
      lines: [head, ...rows, ...more, 'Bấm "Sửa lại" ở khối Cổng AI của bước trước, hoặc "Vẫn tiếp tục" kèm lý do để vượt cổng.'],
      code,
    };
  }

  if (isOverviewWriterCode(code)) {
    const v = overviewErrorView(err, message);   // chỉ lấy nguyên nhân từng dòng (`extra`) — chữ chính là câu riêng của ⑤ ở trên
    return { title: OVERVIEW_WRITER_TITLE[code], lines: [OVERVIEW_WRITER_BODY[code], ...v.extra], exit: 'openOverview', code };
  }

  if (code && SIMPLE_ERRORS[code]) {
    const e = SIMPLE_ERRORS[code];
    return { title: e.title, lines: [e.hint, message].filter((x): x is string => !!x), forceable: e.forceable, code };
  }

  if (message) return { title: 'Không chạy được', lines: [message], code };
  return { title: 'Không chạy được', lines: [status ? `Máy chủ trả lỗi ${status}.` : 'Lỗi không rõ.'], code };
};

// ── Cột "Nối tập" (Y4/Z1) ───────────────────────────────────────────────────────────────────────

export type ContinuityKind = 'ok' | 'dirty' | 'bootstrap';

export interface ContinuityBadge {
  kind: ContinuityKind;
  /** ✓ / ⚠ / ⓘ + chữ ngắn. */
  label: string;
  hint: string;
}

const BADGE: Record<ContinuityKind, { label: string }> = {
  ok: { label: '✓ sạch' },
  dirty: { label: '⚠ cần viết lại' },
  bootstrap: { label: 'ⓘ viết trước nối tập' },
};

const badge = (kind: ContinuityKind, hint: string): ContinuityBadge => ({ kind, label: BADGE[kind].label, hint });

const ts = (s: string | undefined): number => {
  const n = s ? Date.parse(s) : NaN;
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Trạng thái nối tập từng tập của outline `range`. 🔴 CHỈ để định hướng — cổng thật ở máy chủ (client không so băm exit).
 * Chỉ có dòng ⇒ có badge; outline chương (hoặc chưa rõ) ⇒ `{}` ⇒ UI không hiện cột.
 */
export const continuityBadge = (
  rows: EpisodeScriptRecord[] | undefined | null,
  splitMode: 'chapter' | 'range' | undefined,
): Record<number, ContinuityBadge> => {
  const out: Record<number, ContinuityBadge> = {};
  if (splitMode !== 'range') return out;
  const sorted = [...(rows ?? [])].sort((a, b) => a.episode - b.episode);
  const byEp = new Map(sorted.map((r) => [r.episode, r]));
  const isDraft = (r: EpisodeScriptRecord | undefined): r is EpisodeScriptRecord => !!r && r.status === 'DRAFT' && !!r.script;

  for (const row of sorted) {
    const e = row.episode;
    if (!isDraft(row)) { out[e] = badge('dirty', 'Tập này viết hỏng/chưa có kịch bản'); continue; }
    const prior = byEp.get(e - 1);
    const priorBadge = out[e - 1];
    const entry = row.script?.continuity?.entry;
    const hasBinding = !!row.script?.sourceBinding;
    let dirty: string | null = null;
    let bootstrap = false;

    if (e > 1) {
      if (!prior) dirty = 'Chưa có tập ngay trước';
      else if (!isDraft(prior)) dirty = 'Tập ngay trước chưa viết xong';
      else if (entry && entry.predecessorEpisode !== e - 1) dirty = 'Dấu nối trỏ sai tập trước';
      else if (ts(prior.modifiedAt) > ts(row.modifiedAt)) dirty = 'Tập ngay trước được viết lại sau tập này';
      else if (priorBadge?.kind === 'dirty') dirty = 'Một tập trước đó trong chuỗi cần viết lại';
    }
    if (!dirty) {
      if (!row.script?.continuity && hasBinding) bootstrap = true;
      else if (e > 1 && !entry) dirty = 'Thiếu dấu nối với tập trước';
      else if (priorBadge?.kind === 'bootstrap') bootstrap = true;
    }

    out[e] = dirty ? badge('dirty', dirty)
      : bootstrap ? badge('bootstrap', 'Viết trước khi có nối tập — vẫn dùng được, nên viết lại tuần tự nếu cần mạch liền')
        : badge('ok', 'Nối tập sạch');
  }
  return out;
};

/** Yêu cầu "Viết lại tuần tự từ tập X": luôn có `to` (S9b), `force:true`. Không có DRAFT nào / không biết X ⇒ `null` (không cho bấm). */
export const recoverRequest = (
  rows: Array<Pick<EpisodeScriptRecord, 'episode' | 'status'>> | undefined | null,
  recoverFrom: number | undefined,
): { from: number; to: number; force: true } | null => {
  if (!isNum(recoverFrom)) return null;
  const drafts = (rows ?? []).filter((r) => r.status === 'DRAFT').map((r) => r.episode);
  if (!drafts.length) return null;
  return { from: recoverFrom, to: Math.max(...drafts, recoverFrom), force: true };
};

// ── P5d K4 — "Phải giữ khi viết": khoảnh khắc bắt buộc + cặp không gộp ─────────────────────────────

export interface RequiredMomentRow {
  id: string;
  kindLabel: string;
  chapter: number;
  what: string;
  /** Trích đã cắt gọn; rỗng ⇒ không hiển thị. */
  evidence: string;
  /** 261015 P2b-1: trích đã được BE xác minh (neo) — thẻ hiện trích + chương như bằng chứng; false ⇒ trích chưa xác minh (có thể rỗng). */
  anchored: boolean;
  characterNames: string[];
}

export interface KeepSeparateRow {
  a: string;
  b: string;
  aName: string;
  bName: string;
  reason: string;
  evidence: string;
}

export interface MustKeepView {
  moments: RequiredMomentRow[];
  pairs: KeepSeparateRow[];
  /** Cả hai trường có mặt nhưng đều rỗng ⇒ hiện "AI không đề xuất gì". */
  empty: boolean;
}

const EVIDENCE_MAX = 160;

function trimEvidence(text: string | undefined | null): string {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  return t.length > EVIDENCE_MAX ? `${t.slice(0, EVIDENCE_MAX).trimEnd()}…` : t;
}

/** Dựng dòng hiển thị cho card "Phải giữ khi viết". Cả hai trường vắng (outline cũ) ⇒ `null` (ẩn card). */
export function buildMustKeepView(outline: FilmOutline | null | undefined): MustKeepView | null {
  if (!outline) return null;
  if (outline.requiredMoments === undefined && outline.keepSeparate === undefined) return null;
  const nameOf = (id: string): string => {
    const c = (outline.characters ?? []).find((x) => x.id === id);
    return c ? (c.localizedName || c.name || id) : id;
  };
  const moments = [...(outline.requiredMoments ?? [])]
    .sort((x, y) => x.chapter - y.chapter)
    .map((m) => ({
      id: m.id,
      kindLabel: m.kind === 'INTRO_ANCHOR' ? 'Giới thiệu' : 'Cảm xúc',
      chapter: m.chapter,
      what: m.what,
      evidence: trimEvidence(m.evidence),
      anchored: !!m.evidenceAnchor,
      characterNames: (m.characterIds ?? []).map(nameOf),
    }));
  const pairs = (outline.keepSeparate ?? []).map((p) => ({
    a: p.a, b: p.b, aName: nameOf(p.a), bName: nameOf(p.b), reason: p.reason, evidence: trimEvidence(p.evidence),
  }));
  return { moments, pairs, empty: moments.length === 0 && pairs.length === 0 };
}
